import { Grid, Link, Stack, Typography } from "@mui/material"
import {Trans, useTranslation} from "react-i18next"

export const SupportPage = () => {
    const { t } = useTranslation()

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                    <Typography variant="h4">
                        {t("Troubleshooting and support")}
                    </Typography>
                </Stack>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="body1">
                    {t("Errors in SDN can be caused by multiple parties in the chain:")}
                </Typography>
                <ul>
                    <li>{t("Your own organisation (local IT)")}</li>
                    <br/>
                    <li>{t("The service provider (the solution you are accessing)")}</li>
                    <br/>
                    <li>{t("Or the SDN supplier (the network itself)")}</li>
                </ul>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t("Step 1: Start with local troubleshooting")}
                </Typography>
                <br/>
                <Typography>
                    <Trans
                        i18nKey="Always contact <strong>local IT support</strong> first. Their troubleshooting and documentation is the basis for correct escalation."
                        components={{ strong: <strong /> }}
                    />
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t("Step 2: Use the tools to narrow down the error")}
                </Typography>
                <br/>
                <Typography variant="body1">
                    {t("With these tests, you can quickly determine where the problem is:")}
                </Typography>
                <ul>
                    <li>
                        <strong>{t("Check SDN connection (Ping test)")}</strong> – <em>195.80.253.96</em>{" "}
                        <br/>
                        {t("Used for troubleshooting, monitoring and connection control. Only supports ping traffic. Does not depend on external services, making it the most stable test of the SDN connection.")}
                    </li>
                    <br/>
                    <li>
                        <strong>{t("View operational status (Smokeping)")} – </strong>
                        <em>
                            <a href="https://smokeping.dsdn.dk/" target="_blank" rel="noopener noreferrer">smokeping.dsdn.dk</a>
                            {" "}({t("only accessible via SDN")})
                        </em>
                        <br/>
                        {t("Provides an overview of response times and operational status for services enrolled in monitoring.")}
                    </li>
                    <br/>
                    <li>
                        <strong>{t("Confirm SDN access (IP check)")}</strong> – <em>195.80.253.111</em>{" "}
                        <br/>
                        {t("Shows whether you have access to SDN and which SDN IP you are coming from (e.g. to verify correct IP).")}
                    </li>
                    <br/>
                    <li>
                        <strong>{t("Assess where the error is (Ping/trace)")}:</strong>{" "}
                        <br/>
                        {t("Used to assess whether the error is in the network connection or with the service provider.")}
                    </li>
                </ul>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t("Step 3: Contact the SDN supplier if local IT cannot resolve the issue")}
                </Typography>
                <br/>
                <Typography>
                    <Trans
                        i18nKey="If local IT cannot fix the error, contact the SDN supplier's <strong>24/7 staffed SPOC:</strong>"
                        components={{ strong: <strong /> }}
                    />
                </Typography>
                <ul>
                    <li><strong>TDC Erhverv</strong></li>
                    <li>
                        {t("Mail")}{": "}
                        <Link href="mailto:sdn@tdc.dk">sdn@tdc.dk</Link>
                    </li>
                    <li>
                        <Trans
                            i18nKey="Phone: 44 35 80 08 <em>(<strong>always</strong> for urgent matters, and <strong>always only</strong> after a case has been created via email)</em>"
                            components={{strong : <strong/>, em : <em/>}}
                        />
                    </li>
                </ul>
                <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {t("Please provide the following:")}
                </Typography>
                <ul>
                    <li>{t("Organisation name")}</li>
                    <li>{t("Contact person (email and mobile)")}</li>
                    <li>{t("Brief error description")}</li>
                    <li>{t("Test results (preferably with screenshots and relevant logs)")}</li>
                    <li>
                        <Trans
                        i18nKey="State <strong>SDN</strong> as reference"
                        components={{strong: <strong/>}}
                        />
                    </li>
                </ul>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    <Trans
                    i18nKey="Tip: Use a <strong>shared mailbox</strong> as sender if multiple people need to follow the case and replies."
                    components={{strong: <strong/>}}
                    />
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t("Other help or questions?")}
                </Typography>
                <br/>
                <Typography>{t("Contact MedCom:")}</Typography>
                <ul>
                    <li>
                        {t("Mail")}{": "}
                        <Link href="mailto:sdn@medcom.dk">sdn@medcom.dk</Link>
                    </li>
                    <br/>
                    <li>{t("Mon–Thu: 9:00–15:45 and Fri: 9:00–14:00")}</li>
                </ul>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t("Reporting a security incident")}
                </Typography>
                <br/>
                <Typography>
                    {t("If you experience an incident that may concern the security of SDN (e.g. outage, cyberattack or malicious code), report it to:")}
                </Typography>
                <ul>
                    <li>
                        {t("Mail")}{": "}
                        <Link href="mailto:sdn@medcom.dk">sdn@medcom.dk</Link>
                    </li>
                </ul>
                <Typography>
                    <Trans
                    i18nKey="If the incident is <strong>urgent</strong>, contact the operations supplier or MedCom's system management team by phone immediately."
                    components={{strong : <strong/>}}
                    />
                </Typography>
            </Grid>
        </Grid>
    )
}